import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Valores de concepto a nivel factura (POST /invoices/manual/holded).
 */
export const HOLDED_LINE_TYPES = [
  'customer_service',
  'extra_work',
  'intercompany',
  'other',
] as const;

export type HoldedLineType = (typeof HOLDED_LINE_TYPES)[number];

/** Límite de caracteres para `lines.*.desc` (validación alineada con el backend). */
export const HOLDED_LINE_DESC_MAX_LENGTH = 500;

/**
 * Línea: importes, texto auxiliar y, si line_type de la factura es `other`, account por línea.
 */
export interface HoldedInvoiceLinePayload {
  units: number;
  subtotal: number;
  desc: string;
  tags?: string[];
  /** Solo si la factura es line_type === 'other'; obligatorio entonces en cada línea */
  account?: string;
}

/**
 * Body JSON: line_type a nivel factura; account solo dentro de lines[] cuando line_type es other.
 */
export interface CreateHoldedInvoiceData {
  team_id: number;
  line_type: HoldedLineType;
  lines: HoldedInvoiceLinePayload[];
  currency?: string;
  notes?: string;
}

export function buildHoldedLineForApi(
  line: HoldedInvoiceLinePayload,
  invoiceLineType: HoldedLineType
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    units: line.units,
    subtotal: line.subtotal,
    desc: (line.desc ?? '').trim(),
  };
  if (line.tags?.length) body.tags = line.tags;
  if (invoiceLineType === 'other') {
    body.account = line.account?.trim() ?? '';
  }
  return body;
}

export function buildCreateHoldedRequestBody(data: CreateHoldedInvoiceData): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    team_id: data.team_id,
    line_type: data.line_type,
    lines: data.lines.map((line) => buildHoldedLineForApi(line, data.line_type)),
    currency: data.currency || 'EUR',
  };
  const notes = data.notes?.trim();
  if (notes) payload.notes = notes;
  return payload;
}

export interface CreateHoldedInvoiceResponse {
  id: number;
  holded_id?: string;
  team_id: number;
  currency: string;
  notes?: string;
  status: string;
  total: number;
}

/**
 * useCreateHoldedInvoice — POST /invoices/manual/holded (JSON).
 */
export const useCreateHoldedInvoice = () => {
  const { t } = useTranslation('invoicesModule');
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);

  const createHoldedInvoice = async (
    data: CreateHoldedInvoiceData,
    onSuccess?: (response: CreateHoldedInvoiceResponse) => void
  ): Promise<CreateHoldedInvoiceResponse | null> => {
    setIsCreating(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const response = await httpClient.post<CreateHoldedInvoiceResponse>(
        '/invoices/manual/holded',
        buildCreateHoldedRequestBody(data)
      );

      toast({
        title: t('toast.create_holded_invoice_success.title', 'Factura creada en Holded'),
        description: response?.id
          ? t('toast.create_holded_invoice_success.description', `Factura ${response.id} creada exitosamente en Holded`)
          : t('toast.create_holded_invoice_success.description_generic', 'Factura creada exitosamente en Holded'),
        variant: 'default',
      });

      onSuccess?.(response);

      return response;
    } catch (error: any) {
      if (error?.response?.status === 422 && error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0] as string[];

        toast({
          title: t('toast.create_holded_invoice_error.title', 'Error de validación'),
          description: firstError[0] || t('toast.create_holded_invoice_error.validation', 'Datos inválidos'),
          variant: 'destructive',
        });
      } else if (error?.response?.status === 400) {
        toast({
          title: t('toast.create_holded_invoice_error.title', 'Error al crear factura'),
          description:
            error?.response?.data?.message ||
            t('toast.create_holded_invoice_error.invalid_data', 'Datos inválidos para crear factura en Holded'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('toast.create_holded_invoice_error.title', 'Error al crear factura'),
          description:
            error?.message ||
            t('toast.create_holded_invoice_error.unexpected', 'Error inesperado al crear la factura en Holded. Inténtalo de nuevo.'),
          variant: 'destructive',
        });
      }

      return null;
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createHoldedInvoice,
    isCreating,
  };
};
